import React, { useCallback, useEffect, useState } from 'react';
import { addNativeListener, hideSplashScreen } from 'helpers/nativeApp';
import { trackEvent } from 'helpers/Analytics';
import TwineMark from './TwineMark';
import {
  RELOCK_AFTER_MS,
  authenticate,
  checkBiometry,
  getBiometryLabel,
  isBiometricLockEnabled,
} from './biometricLock';
import styles from './NativeApp.module.scss';

// Full page loads remount the shell, so the unlocked state lives in
// sessionStorage: it survives navigation within the WebView but not the app
// process being killed, which is what makes a launch a "cold start".
const SESSION_KEY = 'app-lock-session-active';
const BACKGROUNDED_AT_KEY = 'app-lock-backgrounded-at';

function readSession(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeSession(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // Storage unavailable: the lock falls back to checking on every mount.
  }
}

function clearSession(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    // Ignore
  }
}

function backgroundedLongEnough() {
  const backgroundedAt = Number(readSession(BACKGROUNDED_AT_KEY));
  return !!backgroundedAt && Date.now() - backgroundedAt >= RELOCK_AFTER_MS;
}

/**
 * App-mode behaviour that has to run on every page: hides the native splash
 * once the web app is up, and gates the app behind the biometric lock when
 * the user has turned it on (Settings → App lock).
 */
const NativeAppShell = () => {
  const [locked, setLocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [biometryLabel, setBiometryLabel] = useState('biometrics');

  const unlock = useCallback(async () => {
    setUnlocking(true);
    const ok = await authenticate();
    setUnlocking(false);
    if (ok) {
      writeSession(SESSION_KEY, '1');
      clearSession(BACKGROUNDED_AT_KEY);
      setLocked(false);
      trackEvent('app-lock-unlocked');
    }
  }, []);

  const lockIfEnabled = useCallback(async () => {
    const info = (await isBiometricLockEnabled()) && (await checkBiometry());
    // Biometrics removed from the device since the lock was turned on:
    // don't trap the user behind a lock they can't open.
    if (!info || !info.isAvailable) {
      writeSession(SESSION_KEY, '1');
      return false;
    }
    // Cleared first so a reload while locked locks again.
    clearSession(SESSION_KEY);
    setBiometryLabel(getBiometryLabel(info));
    setLocked(true);
    return true;
  }, []);

  // Cold start: decide on the lock before the splash comes down, so content
  // never flashes before the lock screen. Page loads within an unlocked
  // session skip it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sameSession = readSession(SESSION_KEY) && !backgroundedLongEnough();
      const shouldLock = sameSession ? false : await lockIfEnabled();
      if (cancelled) return;
      await hideSplashScreen();
      if (shouldLock) await unlock();
    })();
    return () => {
      cancelled = true;
    };
  }, [lockIfEnabled, unlock]);

  useEffect(() => {
    const listener = addNativeListener(
      'App',
      'appStateChange',
      async ({ isActive }) => {
        if (!isActive) {
          writeSession(BACKGROUNDED_AT_KEY, String(Date.now()));
          return;
        }
        const relock = backgroundedLongEnough();
        clearSession(BACKGROUNDED_AT_KEY);
        if (relock && (await lockIfEnabled())) await unlock();
      }
    );
    return () => {
      if (listener) listener.remove();
    };
  }, [lockIfEnabled, unlock]);

  if (!locked) return null;

  return (
    <div className={styles.lockScreen} role="dialog" aria-modal="true">
      <TwineMark size={56} className={styles.lockMark} />
      <h1 className={styles.lockTitle}>Twine is locked</h1>
      <p className={styles.lockBody}>
        Unlock with {biometryLabel} to continue.
      </p>
      <button
        type="button"
        className={styles.primaryButton}
        onClick={unlock}
        disabled={unlocking}
      >
        {unlocking ? 'Unlocking…' : 'Unlock'}
      </button>
    </div>
  );
};

export default NativeAppShell;
